import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import { fitnessExercisePresentationName } from '@/lib/fitnessExercisePresentation';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
export interface PerformedWorkoutExercise {
  name: string; muscle: string; timed: boolean; notes?: string; groupId?: string; groupType?: 'superset' | 'circuit';
  sets: { weight: number; reps: number; done: true; type?: 'warmup' | 'working'; rir?: number }[];
}
const valid = (value: unknown, maximum: number, integer = false, minimum = 0): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum && (!integer || Number.isInteger(value));

export function readPerformedWorkoutExercises(value: unknown): PerformedWorkoutExercise[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, LIMITS.exercises).flatMap(item => {
    if (!item || typeof item !== 'object' || typeof item.name !== 'string' || !item.name.trim()
      || item.name.length > LIMITS.name || !Array.isArray(item.sets)) return [];
    const sets = item.sets.slice(0, LIMITS.sets).flatMap(set => set?.done === true
      && valid(set.weight, LIMITS.weight) && valid(set.reps, LIMITS.reps, true, 1)
      ? [{ weight: set.weight, reps: set.reps, done: true as const,
          ...(set.type === 'warmup' || set.type === 'working' ? { type: set.type as 'warmup' | 'working' } : {}),
          ...(valid(set.rir, 10, true) ? { rir: set.rir } : {}),
        }] : []);
    return sets.length ? [{ name: item.name, muscle: typeof item.muscle === 'string' ? item.muscle : '', timed: item.timed === true, sets,
      ...(typeof item.notes === 'string' && item.notes.length <= 2000 ? { notes: item.notes } : {}),
      ...(typeof item.groupId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(item.groupId) && (item.groupType === 'superset' || item.groupType === 'circuit')
        ? { groupId: item.groupId, groupType: item.groupType as 'superset' | 'circuit' } : {}),
    }] : [];
  });
}

const performanceKey = (name: string, muscle: string) => {
  const presentation = fitnessExercisePresentationName(name, muscle), movement = getExplicitFitnessMovement(presentation);
  return movement ? "movement:" + movement.id : "source:" + JSON.stringify([presentation, muscle]);
};

export function previousExercisePerformance(rows: readonly { exercises?: unknown; completed_at?: unknown }[], name: string, muscle: string, at = Date.now()) {
  const identity = performanceKey(name, muscle);
  const ordered = rows.filter(row => typeof row.completed_at === 'string' && Number.isFinite(Date.parse(row.completed_at)) && Date.parse(row.completed_at) <= at)
    .slice().sort((a, b) => Date.parse(String(b.completed_at)) - Date.parse(String(a.completed_at)));
  for (const row of ordered) {
    const matches = readPerformedWorkoutExercises(row.exercises).filter(item => {
      const candidate = performanceKey(item.name, item.muscle);
      return identity && candidate ? identity === candidate : item.name === name && item.muscle === muscle;
    });
    const first = matches[0];
    // Replacements split performed prefixes from remaining work. Rejoin only
    // the same explicit movement, source muscle and unit within this session.
    if (first) return { ...first, sets: matches.filter(item => item.timed === first.timed
      && item.muscle.trim().toLowerCase() === first.muscle.trim().toLowerCase()).flatMap(item => item.sets) };

  }
  return null;
}
